import React, { useState, useEffect, useCallback } from 'react';
import api from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Activity,
  BarChart3,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Play,
  Settings,
  Zap,
  Clock,
  Target,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AgentSummary {
  name: string;
  status: string;
  total_runs: number;
  success_rate: number;
  avg_quality: number;
}

interface AgentMetrics {
  success_rate: number;
  avg_quality: number;
  total_runs: number;
  total_feedback: number;
  avg_latency_ms: number;
}

interface AgentTrend {
  direction: 'up' | 'down' | 'stable';
  change_pct: number;
}

interface HealthData {
  status: string;
  uptime: number;
  version: string;
  checks: Record<string, { status: string; latency_ms?: number; message?: string }>;
}

interface SystemMetrics {
  requests_total: number;
  errors_total: number;
  avg_latency: number;
  cpu_percent: number;
  memory_percent: number;
  active_workers: number;
  endpoint_latencies: { path: string; method: string; avg_ms: number; p99_ms: number; count: number }[];
}

interface FeedbackConfig {
  judge_model: string;
  pass_threshold: number;
  collection_window_hours: number;
  rolling_window_hours: number;
  regression_threshold: number;
  min_samples_for_alert: number;
  optimization_mode: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(seconds: number): string {
  if (seconds < 60) return `${Math.floor(seconds)}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function qualityBadgeVariant(score: number): 'default' | 'secondary' | 'destructive' {
  if (score >= 0.8) return 'default';
  if (score >= 0.5) return 'secondary';
  return 'destructive';
}

function statusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'healthy':
    case 'ok':
    case 'pass':
      return 'text-green-600';
    case 'degraded':
    case 'warning':
      return 'text-yellow-600';
    case 'unhealthy':
    case 'fail':
    case 'error':
      return 'text-red-600';
    default:
      return 'text-muted-foreground';
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function AgentPerformanceCard({
  agent,
  onCollect,
  onOptimize,
}: {
  agent: AgentSummary & { metrics?: AgentMetrics; trend?: AgentTrend };
  onCollect: (name: string) => void;
  onOptimize: (name: string) => void;
}) {
  const successPct = Math.round((agent.metrics?.success_rate ?? agent.success_rate) * 100);
  const quality = agent.metrics?.avg_quality ?? agent.avg_quality;
  const runs = agent.metrics?.total_runs ?? agent.total_runs;
  const trend = agent.trend;

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold truncate">{agent.name}</CardTitle>
          {trend && (
            <span className="flex items-center gap-1 text-sm">
              {trend.direction === 'up' ? (
                <TrendingUp className="h-4 w-4 text-green-600" />
              ) : trend.direction === 'down' ? (
                <TrendingDown className="h-4 w-4 text-red-600" />
              ) : (
                <Activity className="h-4 w-4 text-muted-foreground" />
              )}
              <span
                className={
                  trend.direction === 'up'
                    ? 'text-green-600'
                    : trend.direction === 'down'
                      ? 'text-red-600'
                      : 'text-muted-foreground'
                }
              >
                {trend.change_pct > 0 ? '+' : ''}
                {trend.change_pct.toFixed(1)}%
              </span>
            </span>
          )}
        </div>
        <CardDescription className="text-xs">
          <Badge variant="outline" className="mr-1">
            {agent.status}
          </Badge>
          {runs} total runs
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 space-y-3">
        {/* Success rate */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Success Rate</span>
            <span className="text-xs font-medium">{successPct}%</span>
          </div>
          <Progress value={successPct} className="h-2" />
        </div>

        {/* Quality score */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Avg Quality</span>
          <Badge variant={qualityBadgeVariant(quality)}>
            {quality.toFixed(2)}
          </Badge>
        </div>

        {/* Latency */}
        {agent.metrics?.avg_latency_ms !== undefined && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Avg Latency</span>
            <span className="text-xs font-medium">{Math.round(agent.metrics.avg_latency_ms)}ms</span>
          </div>
        )}

        <Separator />

        {/* Actions */}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => onCollect(agent.name)}>
            <Play className="h-3 w-3 mr-1" />
            Collect Feedback
          </Button>
          <Button variant="default" size="sm" className="flex-1 text-xs" onClick={() => onOptimize(agent.name)}>
            <Zap className="h-3 w-3 mr-1" />
            Optimize
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Tab: Agent Performance
// ---------------------------------------------------------------------------

function AgentPerformanceTab() {
  const { toast } = useToast();
  const [agents, setAgents] = useState<(AgentSummary & { metrics?: AgentMetrics; trend?: AgentTrend })[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/monitoring/agents');
      const agentList: AgentSummary[] = Array.isArray(data) ? data : data.agents ?? [];

      // Fetch metrics and trends in parallel for each agent
      const enriched = await Promise.all(
        agentList.map(async (agent) => {
          let metrics: AgentMetrics | undefined;
          let trend: AgentTrend | undefined;
          try {
            const [metricsRes, trendRes] = await Promise.all([
              api.get('/feedback/agents/' + agent.name + '/metrics'),
              api.get('/feedback/agents/' + agent.name + '/trends'),
            ]);
            metrics = metricsRes.data;
            trend = trendRes.data;
          } catch {
            // Non-critical; leave undefined
          }
          return { ...agent, metrics, trend };
        }),
      );

      setAgents(enriched);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load agents';
      toast({ title: 'Error loading agents', description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleCollect = async (name: string) => {
    setActionLoading((prev) => ({ ...prev, [name + ':collect']: true }));
    try {
      await api.post('/feedback/agents/' + name + '/collect');
      toast({ title: 'Feedback collection triggered', description: `Started collection for ${name}` });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Collection failed';
      toast({ title: 'Collection failed', description: message, variant: 'destructive' });
    } finally {
      setActionLoading((prev) => ({ ...prev, [name + ':collect']: false }));
    }
  };

  const handleOptimize = async (name: string) => {
    setActionLoading((prev) => ({ ...prev, [name + ':optimize']: true }));
    try {
      await api.post('/feedback/agents/' + name + '/optimize');
      toast({ title: 'Optimization triggered', description: `Started optimization for ${name}` });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Optimization failed';
      toast({ title: 'Optimization failed', description: message, variant: 'destructive' });
    } finally {
      setActionLoading((prev) => ({ ...prev, [name + ':optimize']: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading agent performance data...</span>
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          No agents found. Agents will appear here once they have been deployed and have run at least once.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{agents.length} agent(s) tracked</p>
        <Button variant="outline" size="sm" onClick={fetchAgents} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {agents.map((agent) => (
          <AgentPerformanceCard
            key={agent.name}
            agent={agent}
            onCollect={handleCollect}
            onOptimize={handleOptimize}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: System Health
// ---------------------------------------------------------------------------

function SystemHealthTab() {
  const { toast } = useToast();
  const [health, setHealth] = useState<HealthData | null>(null);
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<number>(Date.now());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [healthRes, metricsRes] = await Promise.all([
        api.get('/monitoring/health'),
        api.get('/monitoring/metrics'),
      ]);
      setHealth(healthRes.data);
      setMetrics(metricsRes.data);
      setLastRefresh(Date.now());
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load health data';
      toast({ title: 'Error loading health data', description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 30_000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Relative time ticker
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(tick);
  }, []);

  const elapsedSinceRefresh = (now - lastRefresh) / 1000;

  if (loading && !health) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading system health...</span>
      </div>
    );
  }

  const errorRate =
    metrics && metrics.requests_total > 0
      ? ((metrics.errors_total / metrics.requests_total) * 100).toFixed(2)
      : '0.00';

  return (
    <div className="space-y-6">
      {/* Refresh info */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          <Clock className="inline h-3 w-3 mr-1" />
          Last refreshed {formatRelativeTime(elapsedSinceRefresh)} &middot; Auto-refreshes every 30s
        </p>
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Overall status */}
      {health && (
        <Alert
          className={
            health.status === 'healthy'
              ? 'border-green-500/50 bg-green-50 dark:bg-green-950/20'
              : health.status === 'degraded'
                ? 'border-yellow-500/50 bg-yellow-50 dark:bg-yellow-950/20'
                : 'border-red-500/50 bg-red-50 dark:bg-red-950/20'
          }
        >
          {health.status === 'healthy' ? (
            <CheckCircle className="h-4 w-4 text-green-600" />
          ) : (
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
          )}
          <AlertDescription>
            System is <strong className={statusColor(health.status)}>{health.status}</strong>
            {health.version && <span className="ml-2 text-xs text-muted-foreground">v{health.version}</span>}
            {health.uptime > 0 && (
              <span className="ml-2 text-xs text-muted-foreground">
                &middot; Uptime: {formatUptime(health.uptime)}
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Key metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <Activity className="h-3 w-3" />
              CPU Usage
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.cpu_percent?.toFixed(1) ?? '--'}%</div>
            <Progress value={metrics?.cpu_percent ?? 0} className="h-2 mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <BarChart3 className="h-3 w-3" />
              Memory Usage
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.memory_percent?.toFixed(1) ?? '--'}%</div>
            <Progress value={metrics?.memory_percent ?? 0} className="h-2 mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <Target className="h-3 w-3" />
              Request Rate
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.requests_total?.toLocaleString() ?? '--'}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Avg latency: {metrics?.avg_latency ? `${Math.round(metrics.avg_latency)}ms` : '--'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Error Rate
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${parseFloat(errorRate) > 5 ? 'text-red-600' : parseFloat(errorRate) > 1 ? 'text-yellow-600' : 'text-green-600'}`}>
              {errorRate}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics?.errors_total?.toLocaleString() ?? 0} errors / {metrics?.requests_total?.toLocaleString() ?? 0} total
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Active workers */}
      {metrics?.active_workers !== undefined && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Active Workers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.active_workers}</div>
          </CardContent>
        </Card>
      )}

      {/* Health checks */}
      {health?.checks && Object.keys(health.checks).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Health Checks</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Check</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Latency</TableHead>
                  <TableHead>Message</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(health.checks).map(([name, check]) => (
                  <TableRow key={name}>
                    <TableCell className="font-medium">{name}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          check.status === 'pass' || check.status === 'ok' || check.status === 'healthy'
                            ? 'default'
                            : 'destructive'
                        }
                      >
                        {check.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{check.latency_ms !== undefined ? `${check.latency_ms}ms` : '--'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{check.message ?? '--'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Endpoint latency table */}
      {metrics?.endpoint_latencies && metrics.endpoint_latencies.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">API Endpoint Latency</CardTitle>
            <CardDescription>Performance breakdown by endpoint</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Method</TableHead>
                  <TableHead>Path</TableHead>
                  <TableHead className="text-right">Avg (ms)</TableHead>
                  <TableHead className="text-right">P99 (ms)</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {metrics.endpoint_latencies.map((ep) => (
                  <TableRow key={`${ep.method}-${ep.path}`}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        {ep.method}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{ep.path}</TableCell>
                    <TableCell className="text-right">{Math.round(ep.avg_ms)}</TableCell>
                    <TableCell className="text-right">{Math.round(ep.p99_ms)}</TableCell>
                    <TableCell className="text-right">{ep.count.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Configuration
// ---------------------------------------------------------------------------

const DEFAULT_CONFIG: FeedbackConfig = {
  judge_model: 'gpt-4o',
  pass_threshold: 0.7,
  collection_window_hours: 24,
  rolling_window_hours: 168,
  regression_threshold: 0.1,
  min_samples_for_alert: 10,
  optimization_mode: 'manual',
};

function ConfigurationTab() {
  const { toast } = useToast();
  const [config, setConfig] = useState<FeedbackConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/feedback/config');
      setConfig({ ...DEFAULT_CONFIG, ...data });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load configuration';
      toast({ title: 'Error loading config', description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/feedback/config', config);
      setConfig({ ...DEFAULT_CONFIG, ...data });
      toast({ title: 'Configuration saved', description: 'Feedback configuration has been updated.' });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save configuration';
      toast({ title: 'Save failed', description: message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const updateField = <K extends keyof FeedbackConfig>(key: K, value: FeedbackConfig[K]) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading configuration...</span>
      </div>
    );
  }

  return (
    <Card className="max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Feedback Collection Configuration
        </CardTitle>
        <CardDescription>
          Configure how agent feedback is collected, evaluated, and used for optimization.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Judge Model */}
        <div className="space-y-2">
          <label htmlFor="judge_model" className="text-sm font-medium">
            Judge Model
          </label>
          <Input
            id="judge_model"
            value={config.judge_model}
            onChange={(e) => updateField('judge_model', e.target.value)}
            placeholder="e.g., gpt-4o"
          />
          <p className="text-xs text-muted-foreground">The LLM model used to judge agent output quality.</p>
        </div>

        {/* Pass Threshold */}
        <div className="space-y-2">
          <label htmlFor="pass_threshold" className="text-sm font-medium">
            Pass Threshold
          </label>
          <Input
            id="pass_threshold"
            type="number"
            min={0}
            max={1}
            step={0.05}
            value={config.pass_threshold}
            onChange={(e) => updateField('pass_threshold', parseFloat(e.target.value) || 0)}
          />
          <p className="text-xs text-muted-foreground">
            Minimum quality score (0-1) for a run to be considered passing.
          </p>
        </div>

        {/* Collection Window */}
        <div className="space-y-2">
          <label htmlFor="collection_window_hours" className="text-sm font-medium">
            Collection Window (hours)
          </label>
          <Input
            id="collection_window_hours"
            type="number"
            min={1}
            step={1}
            value={config.collection_window_hours}
            onChange={(e) => updateField('collection_window_hours', parseInt(e.target.value, 10) || 1)}
          />
          <p className="text-xs text-muted-foreground">
            How many hours of recent runs to include when collecting feedback.
          </p>
        </div>

        {/* Rolling Window */}
        <div className="space-y-2">
          <label htmlFor="rolling_window_hours" className="text-sm font-medium">
            Rolling Window (hours)
          </label>
          <Input
            id="rolling_window_hours"
            type="number"
            min={1}
            step={1}
            value={config.rolling_window_hours}
            onChange={(e) => updateField('rolling_window_hours', parseInt(e.target.value, 10) || 1)}
          />
          <p className="text-xs text-muted-foreground">
            Time window for computing rolling metrics and detecting regressions.
          </p>
        </div>

        {/* Regression Threshold */}
        <div className="space-y-2">
          <label htmlFor="regression_threshold" className="text-sm font-medium">
            Regression Threshold
          </label>
          <Input
            id="regression_threshold"
            type="number"
            min={0}
            max={1}
            step={0.01}
            value={config.regression_threshold}
            onChange={(e) => updateField('regression_threshold', parseFloat(e.target.value) || 0)}
          />
          <p className="text-xs text-muted-foreground">
            Quality drop (0-1) that triggers a regression alert.
          </p>
        </div>

        {/* Min Samples for Alert */}
        <div className="space-y-2">
          <label htmlFor="min_samples_for_alert" className="text-sm font-medium">
            Min Samples for Alert
          </label>
          <Input
            id="min_samples_for_alert"
            type="number"
            min={1}
            step={1}
            value={config.min_samples_for_alert}
            onChange={(e) => updateField('min_samples_for_alert', parseInt(e.target.value, 10) || 1)}
          />
          <p className="text-xs text-muted-foreground">
            Minimum number of samples required before alerting on quality changes.
          </p>
        </div>

        {/* Optimization Mode */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Optimization Mode</label>
          <Select value={config.optimization_mode} onValueChange={(v) => updateField('optimization_mode', v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="auto">Auto</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            <strong>Manual:</strong> optimization must be triggered by a user.{' '}
            <strong>Auto:</strong> optimization runs automatically when regressions are detected.
          </p>
        </div>

        <Separator />

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Configuration'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function MonitoringPage() {
  const [activeTab, setActiveTab] = useState('performance');

  return (
    <div className="container mx-auto py-6 px-4 space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <BarChart3 className="h-6 w-6" />
          Monitoring &amp; Feedback
        </h1>
        <p className="text-muted-foreground mt-1">
          Agent performance monitoring, feedback collection, and system health.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="performance" className="flex items-center gap-1">
            <Target className="h-4 w-4" />
            Agent Performance
          </TabsTrigger>
          <TabsTrigger value="health" className="flex items-center gap-1">
            <Activity className="h-4 w-4" />
            System Health
          </TabsTrigger>
          <TabsTrigger value="config" className="flex items-center gap-1">
            <Settings className="h-4 w-4" />
            Configuration
          </TabsTrigger>
        </TabsList>

        <TabsContent value="performance" className="mt-6">
          <AgentPerformanceTab />
        </TabsContent>

        <TabsContent value="health" className="mt-6">
          <SystemHealthTab />
        </TabsContent>

        <TabsContent value="config" className="mt-6">
          <ConfigurationTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default MonitoringPage;
